// components/events/CommitteePanel.jsx
import React, { useMemo, useState, useCallback } from "react";
import { Card, Table, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ROLE_PRESETS, cleanObject, normalizeCommitteeItem } from "../../utils/eventUtils";

/**
 * Props:
 *  - event:      Firestore event doc { id, committee, committeeUids, ... }
 *  - community:  { blocks: [{ name|blockName, flats: [...] }, ...] } (optional but preferred)
 *  - residents:  [{ uid, fullName|displayName, unit: { blockName, flatLabel }, gender, ... }, ...]
 *  - canEditCommittee: boolean (Admin/Manager)
 */
export default function CommitteePanel({ event, community, residents, canEditCommittee }) {
  const eventId = event?.id;
  const auth = getAuth();
  const currentUid = auth.currentUser?.uid || null;

  // ---- UI state ----
  const [addOpen, setAddOpen] = useState(false);
  const [newMember, setNewMember] = useState({
    blockName: "",
    flatLabel: "",
    residentUid: "",
    residentName: "",
    useCustomName: false,
    role: "incharge",
    roleOther: "",
    gender: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* -------------------------------------------
     Helpers: union options + labels
  -------------------------------------------- */

  // Case-insensitive string compare
  const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

  // Extract floor number from flat-like strings: "A-101" -> 1, "B-5-502" -> 5, "502" -> 5
  const extractFloor = (label) => {
    if (!label) return null;
    const nums = String(label).match(/\d+/g);
    if (!nums || nums.length === 0) return null;
    const first = Number(nums[0]);
    if (!Number.isFinite(first)) return null;
    if (first >= 100) return Math.floor(first / 100); // e.g., 502 -> 5
    return first; // e.g., "5-502" -> 5
  };

  // Collect all blocks from community config and resident units
  const blockList = useMemo(() => {
    const set = new Set();

    // 1) From community config (if available)
    const confBlocks = Array.isArray(community?.blocks) ? community.blocks : [];
    confBlocks.forEach((b, idx) => {
      const name = (b?.name || b?.blockName || `Block-${idx + 1}`).trim();
      if (name) set.add(name);
    });

    // 2) From residents
    (Array.isArray(residents) ? residents : []).forEach((u) => {
      const b = (u?.unit?.blockName || "").trim();
      if (b) set.add(b);
    });

    return Array.from(set).sort();
  }, [community, residents]);

  // For the selected block, collect all flats from community and resident units
  const flatListForBlock = useMemo(() => {
    const block = (newMember.blockName || "").trim();
    if (!block) return [];

    const flats = new Set();

    // 1) From community config
    const confBlocks = Array.isArray(community?.blocks) ? community.blocks : [];
    const blk = confBlocks.find(
      (x, idx) => (x?.name || x?.blockName || `Block-${idx + 1}`).trim() === block
    );
    (Array.isArray(blk?.flats) ? blk.flats : []).forEach((f) => {
      const label = (typeof f === "string" ? f : String(f)).trim();
      if (label) flats.add(label);
    });

    // 2) From residents for the same block
    (Array.isArray(residents) ? residents : []).forEach((u) => {
      if ((u?.unit?.blockName || "").trim() === block) {
        const label = (u?.unit?.flatLabel || "").trim();
        if (label) flats.add(label);
      }
    });

    return Array.from(flats)
      .sort()
      .map((label) => ({
        value: label,
        label: label.includes(block) ? label : `${block}-${label}`,
      }));
  }, [community, residents, newMember.blockName]);

  // Resident label helper for the table
  const residentLabelByUid = useCallback(
    (uid) => {
      if (!uid) return "—";
      const u = (Array.isArray(residents) ? residents : []).find((x) => x.uid === uid);
      if (!u) return uid;
      const b = u.unit?.blockName || "";
      const f = u.unit?.flatLabel || "";
      const name = u.fullName || u.displayName || uid;
      return [name, b && f ? `(${b}-${f})` : ""].filter(Boolean).join(" ");
    },
    [residents]
  );

  /* -------------------------------------------
     Sorting: ascending by block
     (optionally by floor, then flat)
  -------------------------------------------- */

  // Determine member's block & flat (prefer resident unit if uid present)
  const getMemberBlock = (m) => {
    if (m?.uid) {
      const u = (Array.isArray(residents) ? residents : []).find((x) => x.uid === m.uid);
      return u?.unit?.blockName || m?.blockName || "";
    }
    return m?.blockName || "";
  };

  const getMemberFlat = (m) => {
    if (m?.uid) {
      const u = (Array.isArray(residents) ? residents : []).find((x) => x.uid === m.uid);
      return u?.unit?.flatLabel || m?.flatLabel || m?.flats || "";
    }
    return m?.flatLabel || m?.flats || "";
  };

  // Sort committee by block (ascending); optionally by floor and flat
  const sortedCommittee = useMemo(() => {
    const list = Array.isArray(event?.committee) ? [...event.committee] : [];
    return list.sort((a, b) => {
      const ablock = getMemberBlock(a);
      const bblock = getMemberBlock(b);

      // If blocks are numeric-like, compare as numbers; else lexicographic
      const an = Number(ablock);
      const bn = Number(bblock);
      const byBlock =
        Number.isFinite(an) && Number.isFinite(bn)
          ? an - bn
          : cmpStr(ablock, bblock);

      if (byBlock !== 0) return byBlock;

      // ---- Optional secondary sorting (uncomment if you want these) ----
      // const afloor = extractFloor(getMemberFlat(a));
      // const bfloor = extractFloor(getMemberFlat(b));
      // if ((afloor ?? Number.POSITIVE_INFINITY) !== (bfloor ?? Number.POSITIVE_INFINITY)) {
      //   return (afloor ?? Number.POSITIVE_INFINITY) - (bfloor ?? Number.POSITIVE_INFINITY);
      // }
      // const byFlat = cmpStr(getMemberFlat(a), getMemberFlat(b));
      // if (byFlat !== 0) return byFlat;

      return 0;
    });
  }, [event?.committee, residents]);

  /* -------------------------------------------
     Actions: Add / Edit / Remove
  -------------------------------------------- */

  const addOrUpdateMember = useCallback(async () => {
    if (!canEditCommittee) return alert("Not allowed for your role.");
    if (!eventId) return alert("Missing eventId.");

    const {
      blockName, flatLabel,
      residentUid, residentName, useCustomName,
      role, roleOther, gender,
    } = newMember;

    if (!blockName) return alert("Please select a Block.");
    if (!flatLabel) return alert("Please select a Flat.");

    const customNameTrim = (residentName || "").trim();
    if (!residentUid && (!useCustomName || !customNameTrim)) {
      return alert("Please select a Resident or enter a custom name.");
    }
    if (!role) return alert("Please select a Role.");
    if (role === "other" && !roleOther.trim()) return alert("Please enter a custom role label.");
    if (!gender) return alert("Please select Gender.");

    try {
      setSaving(true);
      setError("");

      const roleLabel =
        role === "other"
          ? roleOther.trim()
          : ROLE_PRESETS.find((r) => r.value === role)?.label || role;

      const current = Array.isArray(event?.committee) ? [...event.committee] : [];

      // Prepare record
      const record = normalizeCommitteeItem({
        uid: residentUid || undefined,
        residentName: !residentUid && useCustomName ? customNameTrim : undefined,
        role,
        roleLabel,
        gender,
        blockName,
        flatLabel,
      });

      // Upsert by uid or name
      const existingIdx = residentUid
        ? current.findIndex((x) => x?.uid === residentUid)
        : current.findIndex(
            (x) =>
              !x?.uid &&
              (x?.residentName || "").toLowerCase().trim() === customNameTrim.toLowerCase()
          );

      if (existingIdx >= 0) {
        current[existingIdx] = normalizeCommitteeItem({ ...current[existingIdx], ...record });
      } else {
        current.push(record);
      }

      // Deduplicate
      const seenUid = new Set();
      const seenName = new Set();
      const unique = [];
      for (const item of current) {
        const normalized = normalizeCommitteeItem(item);
        if (normalized.uid) {
          if (seenUid.has(normalized.uid)) continue;
          seenUid.add(normalized.uid);
          unique.push(normalized);
        } else {
          const nm = (normalized.residentName || "").toLowerCase().trim();
          if (!nm) continue;
          if (seenName.has(nm)) continue;
          seenName.add(nm);
          unique.push(normalized);
        }
      }

      const payload = cleanObject({
        committee: unique,
        committeeUids: Array.from(seenUid),
        updatedBy: currentUid || null,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "events", eventId), payload);

      // Reset form
      setAddOpen(false);
      setNewMember({
        blockName: "",
        flatLabel: "",
        residentUid: "",
        residentName: "",
        useCustomName: false,
        role: "incharge",
        roleOther: "",
        gender: "",
      });
    } catch (err) {
      console.error("Add/update committee member error:", err);
      setError(err?.message || "Failed to save committee member.");
    } finally {
      setSaving(false);
    }
  }, [newMember, event?.committee, eventId, currentUid, canEditCommittee]);

  const startEdit = useCallback(
    (member) => {
      if (!canEditCommittee) return alert("Not allowed for your role.");
      const u = member?.uid ? (Array.isArray(residents) ? residents : []).find((x) => x.uid === member.uid) : null;
      const b = u?.unit?.blockName || member?.blockName || "";
      const f = u?.unit?.flatLabel || member?.flatLabel || "";
      setNewMember({
        blockName: b,
        flatLabel: f,
        residentUid: member?.uid || "",
        residentName: member?.residentName || "",
        useCustomName: !member?.uid && !!member?.residentName,
        role: member?.role || "incharge",
        roleOther: member?.role === "other" ? member?.roleLabel || "" : "",
        gender: member?.gender || "",
      });
      setAddOpen(true);
    },
    [residents, canEditCommittee]
  );

  const removeByUid = useCallback(
    async (memberUid) => {
      if (!canEditCommittee) return alert("Not allowed for your role.");
      if (!eventId) return alert("Missing eventId.");
      if (!window.confirm("Remove this committee member?")) return;

      try {
        setSaving(true);
        setError("");

        const current = Array.isArray(event?.committee) ? [...event.committee] : [];
        const next = current.filter((m) => m?.uid !== memberUid);

        const seen = new Set(next.map((e) => e?.uid).filter(Boolean));
        const payload = cleanObject({
          committee: next.map(normalizeCommitteeItem),
          committeeUids: Array.from(seen),
          updatedBy: currentUid || null,
          updatedAt: serverTimestamp(),
        });

        await updateDoc(doc(db, "events", eventId), payload);
      } catch (err) {
        console.error("Remove committee member error:", err);
        setError(err?.message || "Failed to remove committee member.");
      } finally {
        setSaving(false);
      }
    },
    [event?.committee, eventId, currentUid, canEditCommittee]
  );

  /* -------------------------------------------
     Render
  -------------------------------------------- */
  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="mb-0">Committee</h6>
          {canEditCommittee && (
            <Button
              className="primary-cta"
              variant={addOpen ? "outline-secondary" : "primary"}
              size="sm"
              onClick={() => setAddOpen((s) => !s)}
            >
              {addOpen ? "Close" : "Add Member"}
            </Button>
          )}
        </div>

        {error && <Alert variant="danger" className="mb-2">{error}</Alert>}

        {addOpen && canEditCommittee && (
          <Card className="border">
            <Card.Body>
              <Row className="g-2">
                {/* Block (union + manual) */}
                <Col md={3}>
                  <Form.Label>Block</Form.Label>
                  <Form.Select
                    value={
                      newMember.blockName
                        ? (blockList.includes(newMember.blockName) ? newMember.blockName : "__manual__")
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__manual__") {
                        setNewMember((s) => ({
                          ...s,
                          blockName: s.blockName || "",
                          flatLabel: "",
                          residentUid: "",
                          residentName: "",
                          useCustomName: false,
                        }));
                      } else {
                        setNewMember((s) => ({
                          ...s,
                          blockName: val,
                          flatLabel: "",
                          residentUid: "",
                          residentName: "",
                          useCustomName: false,
                        }));
                      }
                    }}
                  >
                    <option value="">— Select Block —</option>
                    {blockList.map((b) => (
                      <option key={`block-${b}`} value={b}>{b}</option>
                    ))}
                    <option value="__manual__">Other (enter manually)</option>
                  </Form.Select>

                  {/* Manual block input */}
                  {(newMember.blockName && !blockList.includes(newMember.blockName)) || !newMember.blockName ? (
                    <Form.Control
                      className="mt-2"
                      placeholder="Type block (e.g., A)"
                      value={newMember.blockName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewMember((s) => ({
                          ...s,
                          blockName: val,
                          flatLabel: val ? s.flatLabel : "",
                          residentUid: "",
                          residentName: "",
                          useCustomName: false,
                        }));
                      }}
                    />
                  ) : null}
                </Col>

                {/* Flat (union + manual) */}
                <Col md={3}>
                  <Form.Label>Flat</Form.Label>
                  <Form.Select
                    value={
                      newMember.flatLabel
                        ? (flatListForBlock.some((x) => x.value === newMember.flatLabel) ? newMember.flatLabel : "__manual__")
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__manual__") {
                        setNewMember((s) => ({ ...s, flatLabel: s.flatLabel || "", residentUid: "", residentName: "", useCustomName: false }));
                      } else {
                        setNewMember((s) => ({ ...s, flatLabel: val, residentUid: "", residentName: "", useCustomName: false }));
                      }
                    }}
                    disabled={!newMember.blockName}
                  >
                    <option value="">
                      {newMember.blockName ? "— Select Flat —" : "Select a block first"}
                    </option>
                    {flatListForBlock.map((opt) => (
                      <option key={`flat-${opt.value}`} value={opt.value}>{opt.label}</option>
                    ))}
                    {newMember.blockName && <option value="__manual__">Other (enter manually)</option>}
                  </Form.Select>

                  {/* Manual flat input */}
                  {newMember.blockName && (
                    ((newMember.flatLabel && !flatListForBlock.some((x) => x.value === newMember.flatLabel)) || !newMember.flatLabel) ? (
                      <Form.Control
                        className="mt-2"
                        placeholder={`Type flat (e.g., ${newMember.blockName}-101 or 101)`}
                        value={newMember.flatLabel}
                        onChange={(e) => setNewMember((s) => ({ ...s, flatLabel: e.target.value }))}
                      />
                    ) : null
                  )}
                </Col>

                {/* Resident (or custom) */}
                <Col md={4}>
                  <Form.Label>Resident</Form.Label>
                  <Form.Select
                    value={newMember.useCustomName ? "__custom__" : (newMember.residentUid || "")}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__custom__") {
                        setNewMember((s) => ({
                          ...s,
                          residentUid: "",
                          residentName: "",
                          useCustomName: true,
                        }));
                      } else {
                        setNewMember((s) => ({
                          ...s,
                          residentUid: val,
                          residentName: "",
                          useCustomName: false,
                        }));
                      }
                    }}
                    disabled={!newMember.blockName || !newMember.flatLabel}
                  >
                    <option value="">
                      {newMember.blockName && newMember.flatLabel
                        ? "— Select Resident —"
                        : "Select block & flat first"}
                    </option>
                    {(Array.isArray(residents) ? residents : [])
                      .filter(
                        (u) =>
                          (u.unit?.blockName || "") === newMember.blockName &&
                          (u.unit?.flatLabel || "") === newMember.flatLabel
                      )
                      .map((u, idx) => {
                        const labelParts = [
                          u.fullName || u.displayName || u.uid,
                          u.unit?.blockName && u.unit?.flatLabel ? `(${u.unit.blockName}-${u.unit.flatLabel})` : "",
                        ].filter(Boolean);
                        return (
                          <option key={`resident-opt-${u.uid || "missing"}-${idx}`} value={u.uid || ""}>
                            {labelParts.join(" ")}
                          </option>
                        );
                      })}
                    <option value="__custom__">Other (enter name)</option>
                  </Form.Select>

                  {newMember.useCustomName && (
                    <div className="mt-2">
                      <Form.Label>Custom Name (temporary)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter full name"
                        value={newMember.residentName}
                        onChange={(e) => setNewMember((s) => ({ ...s, residentName: e.target.value }))}
                      />
                      <div className="form-text">
                        Use this when resident isn’t registered yet.
                      </div>
                    </div>
                  )}

                  <div className="form-text">
                    Residents are derived from community members with matching block & flat.
                  </div>
                </Col>

                {/* Role */}
                <Col md={3}>
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={newMember.role}
                    onChange={(e) => setNewMember((s) => ({ ...s, role: e.target.value }))}
                  >
                    {ROLE_PRESETS.map((r) => (
                      <option key={`role-${r.value}`} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                {/* Custom Role */}
                <Col md={3}>
                  <Form.Label>Custom Role</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter custom role"
                    value={newMember.roleOther}
                    onChange={(e) => setNewMember((s) => ({ ...s, roleOther: e.target.value }))}
                    disabled={newMember.role !== "other"}
                  />
                </Col>

                {/* Gender */}
                <Col md={3}>
                  <Form.Label>Gender</Form.Label>
                  <Form.Select
                    value={newMember.gender}
                    onChange={(e) => setNewMember((s) => ({ ...s, gender: e.target.value }))}
                  >
                    <option value="">— Select —</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Form.Select>
                </Col>
              </Row>

              <div className="mt-3 d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={addOrUpdateMember}
                  disabled={
                    saving ||
                    !newMember.blockName ||
                    !newMember.flatLabel ||
                    (!newMember.residentUid && (!newMember.useCustomName || !newMember.residentName?.trim())) ||
                    !newMember.role ||
                    (newMember.role === "other" && !newMember.roleOther.trim()) ||
                    !newMember.gender
                  }
                >
                  {saving ? "Saving…" : "Save Member"}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setAddOpen(false);
                    setNewMember({
                      blockName: "",
                      flatLabel: "",
                      residentUid: "",
                      residentName: "",
                      useCustomName: false,
                      role: "incharge",
                      roleOther: "",
                      gender: "",
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {(event?.committee || []).length === 0 ? (
          <div className="text-muted">No committee assigned.</div>
        ) : (
          <Table bordered responsive size="sm" className="mb-3">
            <thead>
              <tr>
                <th>Resident</th>
                <th>Flat No</th>
                <th>Role</th>
                <th>Gender</th>
                {canEditCommittee && <th style={{ width: 160 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedCommittee.map((m, idx) => (
                <tr key={`committee-row-${m?.uid || m?.residentName || "missing"}-${idx}`}>
                  <td>
                    <code>{m?.uid ? residentLabelByUid(m?.uid) : (m?.residentName || "—")}</code>
                  </td>
                  <td>{m?.uid
                      ? (residents.find(r => r.uid === m.uid)?.unit?.flatLabel || m?.flatLabel || m?.flats || "—")
                      : (m?.flatLabel || m?.flats || "—")
                  }</td>
                  <td>{m?.roleLabel || m?.role || "—"}</td>
                  <td>{m?.gender || "—"}</td>
                  {canEditCommittee && (
                    <td className="d-flex gap-2">
                      <Button
                        className="btn-outline-teal"
                        variant="outline-primary"
                        size="sm"
                        onClick={() => startEdit(m)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeByUid(m?.uid)}
                        disabled={!m?.uid} // Remove works for registered members; custom names can be overwritten by Add
                      >
                        Remove
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
